import { useEffect, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Bookshop } from '../types';

interface DensityMapData {
  bookshops: Bookshop[];
  center: { longitude: number; latitude: number } | null;
  focusLocation: string | null;
  selectedCity: string;
}

interface DensityMapProps {
  data: DensityMapData;
  focusZoom?: number;
  onMapInteraction: () => void;
  mapInteracted: boolean;
}

interface NeighborStore {
  name: string;
  address: string;
}

export function DensityMap({ 
  data, 
  focusZoom = 15, 
  onMapInteraction, 
  mapInteracted 
}: DensityMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const isMoving = useRef<boolean>(false);
  const styleLoaded = useRef<boolean>(false);
  const currentZoom = useRef<number>(12.5);
  const lastCenter = useRef<string | null>(null);
  const currentPopup = useRef<maplibregl.Popup | null>(null);

  // Memoize map feature creators
  const createFeatures = useCallback(() => {
    if (!data?.bookshops?.length) return [];
    
    return data.bookshops.map((shop, index) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [shop.longitude, shop.latitude]
      },
      properties: {
        id: index,
        name: shop.name,
        nearby_stores: shop.nearby_stores ?? 0,
        rating: shop.rating,
        review_count: shop.review_count,
        store_type: shop.store_type,
        neighboring_stores: shop.neighboring_stores ?? '',
        address: shop.address
      }
    }));
  }, [data.bookshops]);

  // Initialize map
  useEffect(() => {
    const container = mapContainer.current;
    if (!container || map.current) return;

    const initializeMap = async () => {
      try {
        map.current = new maplibregl.Map({
          container: mapContainer.current as HTMLElement,
          style: 'https://tiles.stadiamaps.com/styles/stamen_toner.json',
          center: data.center ? [data.center.longitude, data.center.latitude] : [0, 0],
          zoom: currentZoom.current,
          pitch: 0,
          bearing: 0,
          antialias: true,
          maxZoom: 16,
          minZoom: 11,
          localIdeographFontFamily: "'Noto Sans', 'Noto Sans CJK SC', sans-serif",
          attributionControl: {
            compact: true
          },
          preserveDrawingBuffer: true // For screenshot capability
        });

        // Add scale control
        const scale = new maplibregl.ScaleControl({
          maxWidth: 150,
          unit: 'metric'
        });
        map.current.addControl(scale, 'bottom-left');

        // Add keyboard handlers for accessibility
        map.current.keyboard.enable();

        // Add map interaction handlers
        map.current.on('dragstart', () => {
          if (!isMoving.current) {
            onMapInteraction();
          }
        });

        map.current.on('zoomstart', () => {
          if (!isMoving.current) {
            onMapInteraction();
          }
        });

        map.current.on('zoomend', () => {
          if (map.current) {
            const newZoom = map.current.getZoom();
            if (!isMoving.current) {
              currentZoom.current = newZoom;
            }
          }
        });

        // Handle style loading
        map.current.on('style.load', () => {
          styleLoaded.current = true;
          initializeMapData();
        });

      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    initializeMap();

    // Cleanup
    return () => {
      if (currentPopup.current) {
        currentPopup.current.remove();
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      styleLoaded.current = false;
      lastCenter.current = null;
    };
  }, []);

  // Handle city changes
  useEffect(() => {
    if (!map.current || !data.center) return;

    const centerKey = `${data.center.longitude},${data.center.latitude}`;
    
    if (lastCenter.current !== centerKey) {
      isMoving.current = true;
      currentZoom.current = 12.5;

      map.current.flyTo({
        center: [data.center.longitude, data.center.latitude],
        zoom: currentZoom.current,
        duration: 2000,
        essential: true
      });

      setTimeout(() => {
        isMoving.current = false;
      }, 2000);

      lastCenter.current = centerKey;
    }
  }, [data.center]);

  // Handle focus location changes
  useEffect(() => {
    if (!map.current || !data.focusLocation || mapInteracted) return;

    const [lng, lat] = data.focusLocation.split(',').map(Number);
    isMoving.current = true;
    currentZoom.current = focusZoom;

    map.current.flyTo({
      center: [lng, lat],
      zoom: focusZoom,
      padding: { top: 50, bottom: 50, left: 50, right: 50 },
      duration: 1500
    });

    setTimeout(() => {
      isMoving.current = false;
    }, 1500);
  }, [data.focusLocation, focusZoom, mapInteracted]);

  const initializeMapData = useCallback(() => {
    if (!map.current || !data?.bookshops?.length) return;

    try {
      // Update or create source
      const source = map.current.getSource('bookshops') as maplibregl.GeoJSONSource;
      const features = createFeatures();
      
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features
        });
      } else {
        // Add new source and layer
        map.current.addSource('bookshops', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features
          }
        });

        map.current.addLayer({
          id: 'bookshops-circles',
          type: 'circle',
          source: 'bookshops',
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              11, ['*', ['sqrt', ['get', 'nearby_stores']], 10],
              14, ['*', ['sqrt', ['get', 'nearby_stores']], 4]
            ],
            'circle-color': [
              'interpolate',
              ['linear'],
              ['get', 'nearby_stores'],
              2, '#FFA07A',
              5, '#FF7F50',
              8, '#FF6347',
              10, '#FF4500'
            ],
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': [
              'interpolate',
              ['linear'],
              ['zoom'],
              13, 0.8,
              14, 1 
            ]
          }
        });

        setupMapInteractions();
      }
    } catch (error) {
      console.error('Error initializing map data:', error);
    }
  }, [data.bookshops, createFeatures]);

  // Update bookshops data
  useEffect(() => {
    if (!map.current || !styleLoaded.current) return;
    initializeMapData();
  }, [data.bookshops, initializeMapData]);

  const setupMapInteractions = useCallback(() => {
    if (!map.current) return;

    const handleBookshopClick = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      if (!e.features?.[0]?.properties || !e.features[0].geometry || !map.current) return;
      
      const props = e.features[0].properties;
      const geometry = e.features[0].geometry;
      
      if (geometry.type === 'Point') {
        isMoving.current = true;
        const coordinates = geometry.coordinates as [number, number];
        const targetZoom = Math.max(currentZoom.current, 13);

        map.current.flyTo({
          center: coordinates,
          zoom: targetZoom,
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
          duration: 1000
        });

        setTimeout(() => {
          isMoving.current = false;
        }, 1000);
      }

      if (currentPopup.current) {
        currentPopup.current.remove();
      }

      const neighboringStores = props.neighboring_stores
        ? (props.neighboring_stores as string).split('), ')
            .map((store: string) => store.replace(/\)$/, ''))
            .map((store: string) => {
              const match = store.match(/(.*?)\s*\((.*)/);
              return match ? { name: match[1], address: match[2] } : { name: store, address: '' };
            })
        : [];

      currentPopup.current = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: true,
        maxWidth: '300px'
      })
        .setLngLat(e.lngLat)
        .setHTML(`
          <div class="bg-zinc-800 px-4 py-3 rounded-none text-zinc-100 font-mono max-w-sm">
            <div class="font-bold mb-2 text-orange-300">${props.name}</div>
            <div class="text-sm mb-2 text-zinc-400">${props.address}</div>
            ${props.rating ? 
              `<div class="text-sm mb-3">Rating: ${Number(props.rating).toFixed(1)} ★ (${props.review_count} reviews)</div>` 
              : ''}
            <div class="text-sm mb-2">${props.nearby_stores} other bookshops nearby:</div>
            ${neighboringStores.length > 0 ? 
              `<div class="text-xs text-zinc-400 mt-2">
                ${neighboringStores.map((store: NeighborStore) => 
                  `<div class="mb-1 cursor-pointer hover:text-zinc-200" 
                        onclick="document.dispatchEvent(new CustomEvent('neighborClick', {detail: '${store.name}'}))">
                    • ${store.name}${store.address ? ` (${store.address})` : ''}
                  </div>`
                ).join('')}
              </div>`
              : '<div class="text-xs text-zinc-400">No detailed neighbor information available</div>'
            }
          </div>
        `)
        .addTo(map.current);
    };

    const centerOnBookshop = (shopName: string) => {
      const shop = data.bookshops.find(b => b.name === shopName);
      if (!shop || !map.current) return;
      
      isMoving.current = true;
      map.current.flyTo({
        center: [shop.longitude, shop.latitude],
        zoom: Math.max(currentZoom.current, 13),
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        duration: 1000
      });

      setTimeout(() => {
        isMoving.current = false;
      }, 1000);
    };

    // Add interaction handlers
    map.current.on('mouseenter', 'bookshops-circles', () => {
      if (map.current) {
        map.current.getCanvas().style.cursor = 'pointer';
      }
    });

    map.current.on('mouseleave', 'bookshops-circles', () => {
      if (map.current) {
        map.current.getCanvas().style.cursor = '';
      }
    });

    map.current.on('click', 'bookshops-circles', handleBookshopClick);

    // Add custom event listener for neighbor clicks
    document.addEventListener('neighborClick', ((e: CustomEvent) => {
      centerOnBookshop(e.detail);
    }) as EventListener);

  }, [data.bookshops]);

  if (!data?.bookshops?.length) {
    return (
      <div className="mb-16">
        <h2 className="text-sm text-zinc-400 mb-8 font-mono">Bookshop Clusters</h2>
        <div 
          className="h-[70vh] md:h-[60vh] lg:h-[80vh] md:aspect-video bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-none overflow-hidden flex items-center justify-center"
          role="alert"
        >
          <div className="text-zinc-400 font-mono">No bookshop data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-16">
      <h2 className="text-sm text-zinc-400 mb-8 font-mono">Bookshop Clusters</h2>
      <div 
        className="h-[70vh] md:h-[60vh] lg:h-[80vh] bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-none overflow-hidden"
        role="region"
        aria-label={`Interactive map of bookstores in ${data.selectedCity}`}
      >
        <div 
          ref={mapContainer} 
          className="w-full h-full"
          aria-hidden="false"
          tabIndex={0}
        />
      </div>
      <div className="mt-4 text-xs text-zinc-500 font-mono flex items-center gap-4">
        <span>Clusters shrink when zooming in</span>
        <span aria-hidden="true">•</span>
        <span>Click for details</span>
        <span aria-hidden="true">•</span>
        <span>Scale shown in km</span>
      </div>
    </div>
  );
}